import type {
	GroundingEvidence,
	SocialPerspectiveBlueprint,
} from '../../commonTypes';

/** Browser-safe helpers for ideate grounding evidence (no Node / Google API deps). */

export const isSocialPosterProfileEvidence = (item: GroundingEvidence): boolean =>
	item.source.startsWith('social-profile:');

/**
 * Assign a rotated / partitioned subset of shared social poster profiles to one
 * perspective so multi-persona generations stay diverse without extra API Direct calls.
 */
export const pickSocialProfilesForPerspective = (
	profiles: GroundingEvidence[],
	perspectiveIndex: number,
	perspectiveCount: number
): GroundingEvidence[] => {
	if (!profiles.length) return [];
	const count = Math.max(1, perspectiveCount);
	const index = ((perspectiveIndex % count) + count) % count;
	if (profiles.length >= count) {
		const perPerspective = Math.max(1, Math.floor(profiles.length / count));
		const start = index * perPerspective;
		const slice = profiles.slice(start, start + perPerspective);
		return slice.length ? slice.map((item) => ({ ...item })) : [{ ...profiles[index % profiles.length] }];
	}
	return [{ ...profiles[index % profiles.length] }];
};

const socialEvidenceKey = (item: GroundingEvidence): string =>
	item.socialEvidenceId || `${item.source}:${item.url || item.title}`;

const orderedSocialEvidence = (items: GroundingEvidence[]): GroundingEvidence[] =>
	[...items].sort(
		(left, right) =>
			(right.confidence || 0) - (left.confidence || 0) ||
			socialEvidenceKey(left).localeCompare(socialEvidenceKey(right))
	);

/**
 * Give each parallel Ideate perspective a real social lane before generation.
 * The psychographic analysis remains the creative lens; this assignment makes
 * the social stance a grounded constraint rather than an accidental outcome of
 * relevance ordering. Missing lanes are not fabricated.
 */
export const assignSocialPerspectiveBlueprint = (
	profiles: GroundingEvidence[],
	perspectiveIndex: number,
	perspectiveCount: number
): { blueprint?: SocialPerspectiveBlueprint; evidence: GroundingEvidence[] } => {
	const socialProfiles = orderedSocialEvidence(profiles.filter(isSocialPosterProfileEvidence));
	if (!socialProfiles.length) return { evidence: [] };
	const stanceGroups = new Map<string, GroundingEvidence[]>();
	for (const profile of socialProfiles) {
		const stance = profile.stance || 'unclear';
		stanceGroups.set(stance, [...(stanceGroups.get(stance) || []), profile]);
	}
	const stances = [...stanceGroups.entries()]
		.sort(([, left], [, right]) => right.length - left.length || (right[0]?.confidence || 0) - (left[0]?.confidence || 0))
		.map(([stance]) => stance) as SocialPerspectiveBlueprint['primaryStance'][];
	const laneIndex = ((perspectiveIndex % Math.max(1, perspectiveCount)) + stances.length) % stances.length;
	const primaryStance = stances[laneIndex] || 'unclear';
	const lane = stanceGroups.get(primaryStance) || [];
	const laneRound = Math.floor(perspectiveIndex / Math.max(1, stances.length));
	const primaryEvidence = lane.slice(laneRound * 6, laneRound * 6 + 6);
	const laneEvidence = primaryEvidence.length ? primaryEvidence : lane.slice(0, 6);
	const sharedEvidence = socialProfiles
		.filter(
			(profile) =>
				!laneEvidence.some((item) => socialEvidenceKey(item) === socialEvidenceKey(profile)) &&
				['neutral', 'mixed'].includes(profile.sentiment || '')
		)
		.slice(0, 2);
	const counterSignal = socialProfiles.find(
		(profile) =>
			profile.stance &&
			profile.stance !== primaryStance &&
			!sharedEvidence.some((item) => socialEvidenceKey(item) === socialEvidenceKey(profile))
	);
	const evidence = [...laneEvidence, ...sharedEvidence, ...(counterSignal ? [counterSignal] : [])]
		.filter((item, index, list) => list.findIndex((candidate) => socialEvidenceKey(candidate) === socialEvidenceKey(item)) === index)
		.map((item) => ({ ...item }));
	const primary = laneEvidence[0] || socialProfiles[0];
	return {
		blueprint: {
			primarySegmentId: `stance:${primaryStance}`,
			primaryStance,
			sentiment: primary.sentiment,
			emotions: (primary.emotions || []).slice(0, 3),
			evidenceIds: laneEvidence.map(socialEvidenceKey),
			sharedEvidenceIds: sharedEvidence.map(socialEvidenceKey),
			counterSignalEvidenceIds: counterSignal ? [socialEvidenceKey(counterSignal)] : [],
			bundleId: primary.socialBundleId,
			bundleVersion: primary.socialBundleVersion,
		},
		evidence,
	};
};
